import { ChevronRightIcon, CurrencyIcon, isInsideRowDetails, MMButton, RowDetails } from '@components'
import settings from '@config/settings'
import { NAVIGATION_ORIGIN, TABLE_CARDS_MAX_WIDTH } from '@constants'
import { Customize } from '@icons'
import cn from '@integrations/classnames'
import { type KeyboardEvent, type MouseEvent, type ReactNode, useContext } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { formatDisplayDate, uiSize } from '@utils'
import {
  configuration,
  extractCounterpartyName,
  formatTransactionDescription,
  PaymentFlows,
  PaymentMethods,
  PortalUserPermissions,
  paths,
  selectMerchantCapabilities,
  TransactionDirections,
  TransactionStatuses,
  useAppSelector,
  useCurrencies,
  useMyAccessPermissionsQry,
  useMyAccountAccessQry,
  useTransactionColumns
} from 'mmfintech-backend-api'
import type { PaymentFlow, PaymentMethod, TransactionViewPortal } from 'mmfintech-backend-api/types'
import { formatMoney, formatTime, GlobalContext, isValidString, tr } from 'mmfintech-commons'
import { MMTableView } from 'mmfintech-portal-commons'
import { getTransactionAmountSign, getTransactionDisplayAmount } from '../../utils/transactionAmount'
import CustomizeTableModal from './CustomizeTableModal'
import TransactionDropdown from './TransactionDropdown'
import TransactionStatusCell from './TransactionStatusCell'
import styles from './TransactionsTable.module.scss'
import TransactionTypeCell from './TransactionTypeCell'

/** The classes of the scrolling part that holds this table (`TableCard`'s `scrollProps`): they own its cell styles. */
export const TRANSACTIONS_TABLE_SCROLL_CLASS = cn(
  styles['transactions-table-container'],
  'transactions-table-container',
  'transactions-table'
)

const TRANSACTIONS_DEFAULT_VISIBLE_COLUMNS = [
  'id',
  'type',
  'description',
  'reference',
  'status',
  'foreignTransactionId'
] as const

/**
 * Up to this width the table shows its Customize button above the rows (from 1200px the header icon replaces it); the
 * `min-width: 1200px` rule in `TransactionsTable.module.scss` hides it again.
 */
export const CUSTOMIZE_BUTTON_MAX_WIDTH = 1199

interface Props {
  transactions: TransactionViewPortal[]
  reload: () => void
  selectedId?: number
  onSelect?: (transaction: TransactionViewPortal) => void
  /** The list shows cards (phone / tablet): a selected row opens `renderDetails` inline instead of beside the table. */
  cardMode?: boolean
  renderDetails?: (transaction: TransactionViewPortal) => ReactNode
  /** Shown to the right of the Customize button, while that button is shown. */
  toolbarActions?: ReactNode
}

function TransactionsTable({
  transactions,
  reload,
  selectedId,
  onSelect,
  cardMode = false,
  renderDetails,
  toolbarActions
}: Props) {
  const { modalHide, modalShow } = useContext(GlobalContext)

  const { hasAccessTo } = useMyAccessPermissionsQry()
  const { hasAnyWritePermission } = useMyAccountAccessQry()
  const { getCurrencyPrecision } = useCurrencies()

  const capabilities = useAppSelector(selectMerchantCapabilities)
  const { enablePayouts, enableRefunds } = capabilities || {}

  const navigate = useNavigate()
  const columns = useTransactionColumns({
    cookieDomain: configuration.isLocal() ? null : settings.cookieDomain,
    enableCustomerEmail: true,
    enableFailReason: true,
    defaultVisible: [...TRANSACTIONS_DEFAULT_VISIBLE_COLUMNS]
  })

  function handleCustomizeClick() {
    modalShow({
      options: { closeOnClickOutside: false, maxWidth: 450, size: 'medium' },
      content: <CustomizeTableModal columns={columns} onClose={modalHide} />
    })
  }

  // Cards show only the fields that have a value; the table keeps every column.
  function showField(value: unknown) {
    return !cardMode || (value !== undefined && value !== null && value !== '')
  }

  function handleDetailsClick(transactionId: number) {
    navigate(paths.banking.transactions.details(transactionId), { state: { origin: NAVIGATION_ORIGIN.TRANSACTIONS } })
  }

  return (
    <>
      <div className={cn(styles['customize-columns-button'], 'customize-columns-button')}>
        <MMButton
          type='button'
          size='small'
          variant='round-secondary'
          leftIcon={Customize}
          caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMIZE_BUTTON', 'Customize')}
          onClick={handleCustomizeClick}
          data-test='customize-columns'
        />
        {toolbarActions}
      </div>

      <MMTableView.Table scale2={TABLE_CARDS_MAX_WIDTH + 1}>
        <MMTableView.Head>
          <MMTableView.Row>
            {columns.isVisible('id') && (
              <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.ID', 'ID')}</MMTableView.HeadCell>
            )}
            {columns.isVisible('batchFileId') && (
              <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.BATCH_ID', 'Batch ID')}</MMTableView.HeadCell>
            )}
            {columns.isVisible('foreignTransactionId') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.FOREIGN_ID', 'Provided ID')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('account') && (
              <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.ACCOUNT', 'Account')}</MMTableView.HeadCell>
            )}
            <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE_ONLY', 'Date')}</MMTableView.HeadCell>
            <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.TIME', 'Time')}</MMTableView.HeadCell>
            {columns.isVisible('type') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('method') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.PAYMENT_METHOD', 'Payment Method')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('description') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.DESCRIPTION', 'Description')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('customer') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER', 'Counterparty')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('customerEmail') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER_EMAIL', 'Customer email')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('customerName') && (
              <MMTableView.HeadCell>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER_NAME', 'Customer name')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('reference') && (
              <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.REFERENCE', 'Reference')}</MMTableView.HeadCell>
            )}
            <MMTableView.HeadCell className={cn('amount-cell')}>
              {tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}
            </MMTableView.HeadCell>
            <MMTableView.HeadCell>{tr('FRONTEND.TRANSACTIONS.COLUMNS.CURRENCY', 'Currency')}</MMTableView.HeadCell>
            {columns.isVisible('status') && (
              <MMTableView.HeadCell className={cn('thin')}>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status')}
              </MMTableView.HeadCell>
            )}
            {columns.isVisible('failReason') && (
              <MMTableView.HeadCell className={cn('thin')}>
                {tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS_DESCRIPTION', 'Fail reason')}
              </MMTableView.HeadCell>
            )}
            <MMTableView.HeadCell className={cn('buttons')}>
              <MMButton
                type='button'
                variant='secondary'
                collapsed
                collapsedIcon={Customize}
                caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMIZE_BUTTON', 'Customize')}
                title={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMIZE_BUTTON', 'Customize')}
                onClick={handleCustomizeClick}
                data-test='customize-columns'
              />
            </MMTableView.HeadCell>
          </MMTableView.Row>
        </MMTableView.Head>

        <MMTableView.Body>
          {transactions.map((data: TransactionViewPortal) => {
            const {
              id,
              foreignTransactionId,
              status,
              reqTime,
              direction,
              reference,
              accountName,
              paymentMethod,
              trnTypeLocalizationKey,
              checkoutDetails,
              paymentFlow,
              statusCodeLocalizationKey,
              statusCodeDefaultDescription,
              batchId
            } = data

            const { customerName, customerEmail } = checkoutDetails || {}
            const { amount, currency } = getTransactionDisplayAmount(data)

            const showDropdown =
              (enableRefunds || enablePayouts) &&
              (
                [
                  PaymentMethods.BANKWIRE,
                  PaymentMethods.INSTANT_BANK_TRANSFER,
                  PaymentMethods.INTERAC
                ] as PaymentMethod[]
              ).includes(paymentMethod as PaymentMethod) &&
              direction === TransactionDirections.DEPOSIT &&
              ([PaymentFlows.CHECKOUT, PaymentFlows.DEPOSIT] as PaymentFlow[]).includes(paymentFlow as PaymentFlow) &&
              status === TransactionStatuses.PROCESSED

            const selected = id === selectedId
            const description = formatTransactionDescription(data)
            const counterparty = extractCounterpartyName(data)

            // A card shows the control on the title's row (to its right); the table keeps it in its last column.
            const actionsInTitle = cardMode && columns.isVisible('type')

            const rowActions = (
              <div
                className={cn(styles['details-inner'], 'details-inner')}
                onClick={showDropdown ? (e) => e.stopPropagation() : undefined}
              >
                {showDropdown ? (
                  <TransactionDropdown
                    transaction={data}
                    reload={reload}
                    enablePayouts={enablePayouts && hasAnyWritePermission}
                    enableRefunds={enableRefunds && hasAccessTo(PortalUserPermissions.REFUNDS)}
                    handleDetailsClick={handleDetailsClick}
                  />
                ) : (
                  <ChevronRightIcon
                    className={cn(styles['chevron'], 'chevron', {
                      'chevron--card': cardMode,
                      'chevron--open': cardMode && selected
                    })}
                  />
                )}
              </div>
            )

            function selectRow() {
              if (onSelect) onSelect(data)
              else handleDetailsClick(id)
            }

            function handleRowClick(event: MouseEvent) {
              if (isInsideRowDetails(event)) return
              selectRow()
            }

            function handleRowKeyDown(event: KeyboardEvent) {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                selectRow()
              }
            }

            return (
              <MMTableView.Row
                key={id}
                className={cn('clickable', { selected })}
                onClick={handleRowClick}
                {...(cardMode ? { tabIndex: 0, 'aria-expanded': selected, onKeyDown: handleRowKeyDown } : {})}
                data-test='table-row'
              >
                {columns.isVisible('id') && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.ID', 'ID')} className={cn('tx-id')}>
                    <span className={cn('table-cell-strong')}>{id}</span>
                  </MMTableView.Cell>
                )}
                {columns.isVisible('batchFileId') && showField(batchId) && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.BATCH_ID', 'Batch ID')}>
                    {batchId}
                  </MMTableView.Cell>
                )}
                {columns.isVisible('foreignTransactionId') && showField(foreignTransactionId) && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.FOREIGN_ID', 'Provided ID')}>
                    <span className={cn(styles['ellipsis'], 'ellipsis')} title={foreignTransactionId}>
                      {foreignTransactionId}
                    </span>
                  </MMTableView.Cell>
                )}
                {columns.isVisible('account') && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.ACCOUNT', 'Account')}>
                    {accountName}
                  </MMTableView.Cell>
                )}
                <MMTableView.Cell
                  caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE_ONLY', 'Date')}
                  className={cn('no-wrap', 'tx-date')}
                >
                  <span className={cn('text-nowrap')}>{formatDisplayDate(reqTime)}</span>
                </MMTableView.Cell>
                <MMTableView.Cell
                  caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.TIME', 'Time')}
                  className={cn('no-wrap', 'tx-time')}
                >
                  <span className={cn('text-nowrap')}>{formatTime(reqTime)}</span>
                </MMTableView.Cell>
                {columns.isVisible('type') && (
                  <MMTableView.Cell
                    caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}
                    className={cn('tx-type')}
                  >
                    {actionsInTitle ? (
                      <div className={cn(styles['tx-type-row'], 'tx-type-row')}>
                        <TransactionTypeCell direction={direction} trnTypeLocalizationKey={trnTypeLocalizationKey} />
                        {rowActions}
                      </div>
                    ) : (
                      <TransactionTypeCell direction={direction} trnTypeLocalizationKey={trnTypeLocalizationKey} />
                    )}
                  </MMTableView.Cell>
                )}
                {columns.isVisible('method') && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.PAYMENT_METHOD', 'Payment Method')}>
                    {tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)}
                  </MMTableView.Cell>
                )}
                {!cardMode && columns.isVisible('description') && (
                  <MMTableView.Cell
                    caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.DESCRIPTION', 'Description')}
                    className={cn('tx-wide')}
                  >
                    <span className={cn(styles['ellipsis'], 'ellipsis')} title={cardMode ? undefined : description}>
                      {description}
                    </span>
                  </MMTableView.Cell>
                )}
                {columns.isVisible('customer') && showField(counterparty) && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER', 'Counterparty')}>
                    {counterparty}
                  </MMTableView.Cell>
                )}
                {columns.isVisible('customerEmail') && showField(customerEmail) && (
                  <MMTableView.Cell
                    caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER_EMAIL', 'Customer email')}
                    className={cn('tx-wide')}
                  >
                    <span className={cn(styles['ellipsis'], 'ellipsis')} title={customerEmail}>
                      {customerEmail}
                    </span>
                  </MMTableView.Cell>
                )}
                {columns.isVisible('customerName') && showField(customerName) && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER_NAME', 'Customer name')}>
                    {customerName}
                  </MMTableView.Cell>
                )}
                {!cardMode && columns.isVisible('reference') && (
                  <MMTableView.Cell caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.REFERENCE', 'Reference')}>
                    <span className={cn(styles['ellipsis'], 'ellipsis')} title={reference}>
                      {reference}
                    </span>
                  </MMTableView.Cell>
                )}
                <MMTableView.Cell
                  caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}
                  className={cn('amount-cell')}
                >
                  <span
                    className={cn(styles['amount'], 'amount', {
                      [styles['muted']]: status === TransactionStatuses.FAILED,
                      muted: status === TransactionStatuses.FAILED
                    })}
                  >
                    {getTransactionAmountSign(direction, status)}
                    {formatMoney(amount, '', getCurrencyPrecision(currency)).trim()}
                  </span>
                </MMTableView.Cell>
                <MMTableView.Cell
                  caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.CURRENCY', 'Currency')}
                  className={cn('tx-currency')}
                >
                  <span className={cn(styles['currency-cell'], 'currency-cell')}>
                    <CurrencyIcon currency={currency} size={uiSize(16)} circle />
                    {currency}
                  </span>
                </MMTableView.Cell>
                {columns.isVisible('status') && (
                  <MMTableView.Cell
                    caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status')}
                    className={cn('status-cell')}
                  >
                    <TransactionStatusCell status={status} />
                  </MMTableView.Cell>
                )}
                {columns.isVisible('failReason') &&
                  showField(statusCodeLocalizationKey || statusCodeDefaultDescription) && (
                    <MMTableView.Cell
                      caption={tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS_DESCRIPTION', 'Fail reason')}
                      className={cn('tx-wide')}
                    >
                      {isValidString(statusCodeLocalizationKey)
                        ? tr(statusCodeLocalizationKey, statusCodeDefaultDescription)
                        : statusCodeDefaultDescription}
                    </MMTableView.Cell>
                  )}
                {!actionsInTitle && (
                  <MMTableView.Cell className={cn('preview-cell no-border', { 'row-chevron-card': cardMode })}>
                    {rowActions}
                  </MMTableView.Cell>
                )}
                {cardMode && renderDetails && <RowDetails open={selected}>{renderDetails(data)}</RowDetails>}
              </MMTableView.Row>
            )
          })}
        </MMTableView.Body>
      </MMTableView.Table>
    </>
  )
}

export default TransactionsTable
